
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, HelpCircle } from 'lucide-react';
import { 
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LeadScore } from '@/types/leadScore';
import { getLeadScoreCategory } from '@/services/leads/leadScoringService';
import { format } from 'date-fns';

interface LeadScoreCardProps {
  leadScore: LeadScore | null;
  isLoading: boolean;
  isCalculating: boolean;
  onRecalculate: () => void;
  className?: string;
}

const LeadScoreCard: React.FC<LeadScoreCardProps> = ({
  leadScore,
  isLoading,
  isCalculating,
  onRecalculate,
  className
}) => {
  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-xl font-semibold">Lead Score</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-2 w-full mb-2" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  // If there's no score yet
  if (!leadScore) {
    return (
      <Card className={className}>
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-xl font-semibold">Lead Score</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-center py-4">
            <p className="text-sm text-muted-foreground mb-3">No score calculated yet</p>
            <Button 
              onClick={onRecalculate} 
              disabled={isCalculating}
              variant="outline"
              size="sm"
            >
              {isCalculating ? (
                <>
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                  Calculating...
                </>
              ) : (
                <>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Calculate Score
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const scoreCategory = getLeadScoreCategory(leadScore.totalScore);
  
  return (
    <Card className={className}>
      <CardHeader className="space-y-1 pb-4">
        <div className="flex justify-between items-center">
          <CardTitle className="text-xl font-semibold">Lead Score</CardTitle>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="ghost" size="icon" className="h-6 w-6">
                  <HelpCircle className="h-4 w-4 text-muted-foreground" />
                </Button>
              </TooltipTrigger>
              <TooltipContent className="max-w-sm">
                <p>Lead score is calculated based on demographic information, engagement level, activity history, and recency of interactions.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center">
          <div>
            <span className="text-3xl font-bold">{leadScore.totalScore}</span>
            <span className="text-sm text-muted-foreground ml-2">/ 100</span>
          </div>
          <Badge className={`${scoreCategory.color} text-white`}>
            {scoreCategory.label}
          </Badge>
        </div>
        
        <Progress value={leadScore.totalScore} className="h-2" />
        
        <div className="grid grid-cols-2 gap-2 text-sm">
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Demographic</div>
            <div className="font-medium">{leadScore.demographicScore}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Engagement</div>
            <div className="font-medium">{leadScore.engagementScore}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Activity</div>
            <div className="font-medium">{leadScore.activityScore}</div>
          </div>
          <div className="space-y-1">
            <div className="text-xs text-muted-foreground">Recency</div>
            <div className="font-medium">{leadScore.recencyScore}</div>
          </div>
        </div>
        
        <div className="flex justify-between items-center text-xs text-muted-foreground pt-2 border-t">
          <span>Last updated: {leadScore.lastCalculated ? format(new Date(leadScore.lastCalculated), 'MMM d, yyyy') : 'N/A'}</span>
          <Button 
            onClick={onRecalculate} 
            disabled={isCalculating}
            variant="ghost" 
            size="sm"
            className="h-7 px-2"
          >
            {isCalculating ? (
              <RefreshCw className="h-3 w-3 animate-spin" />
            ) : (
              <RefreshCw className="h-3 w-3" />
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default LeadScoreCard;
